import React from 'react';
import { SVGShape } from '../../types';

/**
 * Renders an SVGShape as a single SVG `<image>` element.
 *
 * Background: the previous renderer fetched the SVG text and inlined it via
 * `dangerouslySetInnerHTML`, which exploded a CAD-export floor plan into
 * tens of thousands of DOM nodes — the browser had to layout/paint/layerize
 * the lot on every change, dragging the page to ~1 fps. Switching to
 * `<image href="…">` keeps it as one referenced asset that the browser
 * decodes once and composites cheaply, regardless of source complexity.
 */
export const SVGShapeRenderer: React.FC<{ shape: SVGShape }> = ({ shape }) => {
  const opacity = shape.hidden ? 0 : 1;
  const scaleX = shape.scaleX || 1;
  const scaleY = shape.scaleY || 1;
  const rotation = shape.rotation || 0;

  const href = React.useMemo(() => {
    // Inline content takes precedence — turn it into a data URL so the
    // browser fetches it without a network round trip. encodeURIComponent
    // + unescape handles non-Latin1 (Asian text labels, etc.) before btoa.
    if (shape.svgContent) {
      const encoded = btoa(
        unescape(encodeURIComponent(shape.svgContent))
      );
      return `data:image/svg+xml;base64,${encoded}`;
    }
    if (!shape.path) return null;
    if (shape.path.startsWith('/')) {
      // Resolve site-relative paths against the page origin so the SVG can
      // be loaded from outside the web app (e.g. file:// kiosk shells).
      const base =
        typeof window !== 'undefined' && window.location.protocol === 'file:'
          ? 'https://app.gettactic.com'
          : '';
      return `${base}${shape.path}`;
    }
    return shape.path;
  }, [shape.path, shape.svgContent]);

  if (!href) return null;

  // Match Konva's `T(x,y) · R(θ) · S(sX, sY)` pipeline. Only emit a transform
  // when actually needed to keep the DOM small for the common case.
  const needsTransform = scaleX !== 1 || scaleY !== 1 || rotation !== 0;
  const rotPart = rotation !== 0 ? ` rotate(${rotation})` : '';
  const scalePart = scaleX !== 1 || scaleY !== 1 ? ` scale(${scaleX}, ${scaleY})` : '';
  const transform = needsTransform
    ? `translate(${shape.x}, ${shape.y})${rotPart}${scalePart}`
    : undefined;
  const positionProps = needsTransform
    ? { x: 0, y: 0 }
    : { x: shape.x, y: shape.y };

  const image = (
    <image
      {...positionProps}
      width={shape.width}
      height={shape.height}
      href={href}
      preserveAspectRatio="none"
      opacity={opacity !== 1 ? opacity : undefined}
    />
  );

  return needsTransform && transform ? <g transform={transform}>{image}</g> : image;
};
